import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Menu, X, Truck } from "lucide-react";

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const [prevPath, setPrevPath] = useState(location.pathname);

  if (prevPath !== location.pathname) {
    setPrevPath(location.pathname);
    if (open) setOpen(false);
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: t.nav.home, tid: "nav-home-link" },
    { to: "/team", label: t.nav.team, tid: "nav-team-link" },
    { to: "/gallery", label: t.nav.gallery, tid: "nav-gallery-link" },
    { to: "/quote", label: t.nav.quote, tid: "nav-quote-link" },
  ];

  const linkClass = ({ isActive }) =>
    `text-xs tracking-[0.25em] uppercase transition-colors ${
      isActive ? "text-white" : "text-zinc-400 hover:text-white"
    }`;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/85 backdrop-blur-xl border-b border-zinc-800"
          : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-16 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center gap-3 group"
        >
          <div className="w-8 h-8 bg-[#ff5a00] flex items-center justify-center">
            <Truck className="w-4 h-4 text-black" strokeWidth={2.5} />
          </div>
          <div className="font-display text-xl md:text-2xl leading-none tracking-wider">
            BRÄNNINGER
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 mt-0.5 font-mono">
              ÅKERI &amp; LOGISTIK AB
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.tid}
              className={linkClass}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div
            className="flex items-center border border-zinc-700 text-xs font-mono"
            data-testid="lang-toggle-btn"
          >
            <button
              onClick={() => setLang("sv")}
              data-testid="lang-sv"
              className={`px-2.5 py-1.5 transition-colors ${
                lang === "sv"
                  ? "bg-[#ff5a00] text-black"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              SV
            </button>
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-2.5 py-1.5 transition-colors ${
                lang === "en"
                  ? "bg-[#ff5a00] text-black"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              EN
            </button>
          </div>

          <Link
            to="/quote"
            data-testid="navbar-quote-cta"
            className="hidden md:inline-flex items-center px-5 py-2.5 bg-[#ff5a00] text-black text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#ff7a30] transition-colors"
          >
            {t.common.cta_quote}
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden text-white"
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-black border-t border-zinc-800">
          <nav className="flex flex-col px-6 py-6 gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-${l.tid}`}
                className={({ isActive }) =>
                  `text-base tracking-widest uppercase ${
                    isActive ? "text-[#ff5a00]" : "text-zinc-300"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/quote"
              data-testid="mobile-quote-cta"
              className="mt-2 inline-flex items-center justify-center px-5 py-3 bg-[#ff5a00] text-black text-sm tracking-widest uppercase font-bold"
            >
              {t.common.cta_quote}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
